import React, { useState } from 'react';
import { Calendar, Mail, Phone, User } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    startDate: '',
    endDate: '',
    message: ''
  });

  const [submitStatus, setSubmitStatus] = useState({
    status: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ status: 'loading', message: 'Sende Anfrage...' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      // First try to get the response as text
      const responseText = await response.text();
      let data;
      
      try {
        // Then try to parse it as JSON
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Server response:', responseText);
        throw new Error('Server response was not valid JSON');
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.status === 'success') {
        setSubmitStatus({
          status: 'success',
          message: 'Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns in Kürze bei Ihnen.'
        });
        
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          startDate: '',
          endDate: '',
          message: ''
        });
      } else {
        throw new Error(data.message || 'Ein unbekannter Fehler ist aufgetreten');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        status: 'error',
        message: `Entschuldigung, es gab einen Fehler beim Senden Ihrer Anfrage: ${error.message}`
      });
    }
  };

  return (
    <div className="min-h-screen bg-provence-limestone py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-serif font-bold text-provence-stone mb-4">
              Kontakt aufnehmen
            </h1>
            <p className="text-provence-stone/70">
              Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.
            </p>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 space-y-8">
            {/* Status Message */}
            {submitStatus.message && (
              <div className={`p-4 rounded-md ${
                submitStatus.status === 'success' ? 'bg-green-50 text-green-800' :
                submitStatus.status === 'error' ? 'bg-red-50 text-red-800' :
                'bg-blue-50 text-blue-800'
              }`}>
                {submitStatus.message}
              </div>
            )}

            {/* Name Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="firstName" className="block text-sm font-medium text-provence-stone">
                  Vorname
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-provence-lavender" />
                  </div>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="block w-full pl-10 rounded-md border-0 py-3 text-provence-stone shadow-sm ring-1 ring-inset ring-provence-lavender/20 placeholder:text-provence-stone/60 focus:ring-2 focus:ring-inset focus:ring-provence-lavender"
                    placeholder="Max"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="lastName" className="block text-sm font-medium text-provence-stone">
                  Nachname
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-provence-lavender" />
                  </div>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="block w-full pl-10 rounded-md border-0 py-3 text-provence-stone shadow-sm ring-1 ring-inset ring-provence-lavender/20 placeholder:text-provence-stone/60 focus:ring-2 focus:ring-inset focus:ring-provence-lavender"
                    placeholder="Mustermann"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-provence-stone">
                  E-Mail
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-provence-lavender" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="block w-full pl-10 rounded-md border-0 py-3 text-provence-stone shadow-sm ring-1 ring-inset ring-provence-lavender/20 placeholder:text-provence-stone/60 focus:ring-2 focus:ring-inset focus:ring-provence-lavender"
                    placeholder="max.mustermann@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="block text-sm font-medium text-provence-stone">
                  Telefon
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-provence-lavender" />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="block w-full pl-10 rounded-md border-0 py-3 text-provence-stone shadow-sm ring-1 ring-inset ring-provence-lavender/20 placeholder:text-provence-stone/60 focus:ring-2 focus:ring-inset focus:ring-provence-lavender"
                    placeholder="+49 123 45678900"
                  />
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="startDate" className="block text-sm font-medium text-provence-stone">
                  Anreisedatum
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-provence-lavender" />
                  </div>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                    className="block w-full pl-10 rounded-md border-0 py-3 text-provence-stone shadow-sm ring-1 ring-inset ring-provence-lavender/20 placeholder:text-provence-stone/60 focus:ring-2 focus:ring-inset focus:ring-provence-lavender"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="endDate" className="block text-sm font-medium text-provence-stone">
                  Abreisedatum
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-provence-lavender" />
                  </div>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                    className="block w-full pl-10 rounded-md border-0 py-3 text-provence-stone shadow-sm ring-1 ring-inset ring-provence-lavender/20 placeholder:text-provence-stone/60 focus:ring-2 focus:ring-inset focus:ring-provence-lavender"
                  />
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label htmlFor="message" className="block text-sm font-medium text-provence-stone">
                Nachricht (optional)
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="block w-full rounded-md border-0 py-3 text-provence-stone shadow-sm ring-1 ring-inset ring-provence-lavender/20 placeholder:text-provence-stone/60 focus:ring-2 focus:ring-inset focus:ring-provence-lavender"
                placeholder="Ihre Nachricht an uns..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitStatus.status === 'loading'}
                className="inline-flex items-center justify-center rounded-md text-base font-serif transition-all 
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-provence-lavender
                  bg-provence-lavender text-white shadow-md hover:bg-provence-lavender/90 
                  hover:shadow-lg px-8 py-3 border border-provence-lavender/20
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitStatus.status === 'loading' ? 'Wird gesendet...' : 'Anfrage senden'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact; 